import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { ChevronLeft, User, MapPin, CreditCard, Camera } from 'lucide-react';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ProfileSettings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [contractorProfile, setContractorProfile] = useState(null);

  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    country: user?.country || 'UK'
  });

  const [contractorData, setContractorData] = useState({
    company_name: '',
    service_radius_km: 10,
    base_address: ''
  });

  useEffect(() => {
    if (user?.role === 'contractor') {
      fetchContractorProfile();
    }
    fetchAddresses();
  }, []);

  const fetchContractorProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/contractors/profile`);
      setContractorProfile(response.data);
      setContractorData({
        company_name: response.data.company_name || '',
        service_radius_km: response.data.service_radius_km || 10,
        base_address: ''
      });
    } catch (error) {
      console.error('Failed to load contractor profile');
    }
  };

  const fetchAddresses = async () => {
    try {
      const response = await axios.get(`${API_URL}/addresses`);
      setAddresses(response.data);
    } catch (error) {
      console.error('Failed to load addresses');
    }
  };

  const handleProfileUpdate = async () => {
    setLoading(true);
    try {
      toast.success('Profile updated!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleContractorUpdate = async () => {
    setLoading(true);
    try {
      await axios.patch(`${API_URL}/contractors/profile`, {
        company_name: contractorData.company_name,
        service_radius_km: contractorData.service_radius_km
      });
      toast.success('Contractor profile updated!');
      fetchContractorProfile();
    } catch (error) {
      toast.error('Failed to update contractor profile');
    } finally {
      setLoading(false);
    }
  };

  const convertKmToMiles = (km) => {
    return (km * 0.621371).toFixed(1);
  };

  const convertMilesToKm = (miles) => {
    return (miles / 0.621371).toFixed(1);
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="text-neutral-600 hover:text-neutral-900">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Settings</h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="addresses">Addresses</TabsTrigger>
            {user?.role === 'customer' ? (
              <TabsTrigger value="payment">Payment</TabsTrigger>
            ) : (
              <TabsTrigger value="contractor">Contractor</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="profile">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl border border-neutral-200 p-8 space-y-6 mt-4"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-24 h-24 bg-neutral-200 rounded-full flex items-center justify-center">
                  <Camera className="w-8 h-8 text-neutral-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{user?.name}</h3>
                  <p className="text-sm text-neutral-600">{user?.email}</p>
                  <Button variant="outline" size="sm" className="mt-2">
                    Upload Photo
                  </Button>
                </div>
              </div>

              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  value={profileData.name}
                  onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  className="h-12"
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  value={profileData.phone}
                  onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                  className="h-12"
                />
              </div>

              <div>
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={profileData.country}
                  onChange={(e) => setProfileData({ ...profileData, country: e.target.value })}
                  className="h-12"
                />
              </div>

              <Button
                onClick={handleProfileUpdate}
                disabled={loading}
                className="w-full bg-[#007AFF] hover:bg-[#0066CC] text-white rounded-full h-12"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </motion.div>
          </TabsContent>

          <TabsContent value="addresses">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4 mt-4"
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Your Addresses</h3>
                <Button onClick={() => navigate('/addresses/new')}>
                  <MapPin className="w-4 h-4 mr-2" />
                  Your Location
                </Button>
              </div>

              {addresses.length === 0 ? (
                <div className="bg-white rounded-xl border border-neutral-200 p-8 text-center">
                  <p className="text-neutral-600 mb-4">No addresses saved</p>
                  <Button onClick={() => navigate('/addresses/new')}>Add your first address</Button>
                </div>
              ) : (
                addresses.map((addr) => (
                  <div key={addr.id} className="bg-white rounded-xl border border-neutral-200 p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-semibold mb-1">{addr.label}</h4>
                        <p className="text-sm text-neutral-600">
                          {addr.address_line_1}
                          {addr.address_line_2 && `, ${addr.address_line_2}`}
                        </p>
                        <p className="text-sm text-neutral-600">
                          {addr.city}, {addr.postal_code}
                        </p>
                        {addr.is_default && (
                          <span className="inline-block mt-2 px-2 py-1 bg-[#007AFF] text-white text-xs rounded-full">
                            Default
                          </span>
                        )}
                      </div>
                      <Button variant="outline" size="sm">Edit</Button>
                    </div>
                  </div>
                ))
              )}
            </motion.div>
          </TabsContent>

          {user?.role === 'customer' && (
            <TabsContent value="payment">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl border border-neutral-200 p-8 mt-4"
              >
                <div className="flex items-center gap-3 mb-6">
                  <CreditCard className="w-6 h-6 text-[#007AFF]" />
                  <h3 className="text-lg font-semibold">Payment Methods</h3>
                </div>

                <div className="space-y-4">
                  <div className="border border-neutral-200 rounded-lg p-4 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-8 bg-neutral-200 rounded flex items-center justify-center">
                        <span className="text-xs font-semibold">VISA</span>
                      </div>
                      <div>
                        <p className="font-semibold">•••• 4242</p>
                        <p className="text-sm text-neutral-600">Expires 12/26</p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm">Remove</Button>
                  </div>

                  <Button variant="outline" className="w-full">
                    <CreditCard className="w-4 h-4 mr-2" />
                    Add Payment Method
                  </Button>
                </div>

                <p className="text-xs text-neutral-500 mt-6">
                  Your payment information is securely stored and encrypted. We never share your card details.
                </p>
              </motion.div>
            </TabsContent>
          )}

          {user?.role === 'contractor' && (
            <TabsContent value="contractor">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl border border-neutral-200 p-8 space-y-6 mt-4"
              >
                <div>
                  <Label htmlFor="company_name">Company Name</Label>
                  <Input
                    id="company_name"
                    value={contractorData.company_name}
                    onChange={(e) => setContractorData({ ...contractorData, company_name: e.target.value })}
                    className="h-12"
                  />
                </div>

                <div>
                  <Label htmlFor="service_radius">
                    Service Radius ({user?.country === 'UK' ? 'Miles' : 'Kilometers'})
                  </Label>
                  <Input
                    id="service_radius"
                    type="number"
                    value={user?.country === 'UK' ? convertKmToMiles(contractorData.service_radius_km) : contractorData.service_radius_km}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);
                      setContractorData({
                        ...contractorData,
                        service_radius_km: user?.country === 'UK' ? convertMilesToKm(value) : value
                      });
                    }}
                    className="h-12"
                  />
                  <p className="text-sm text-neutral-600 mt-1">
                    You will receive jobs within {user?.country === 'UK' ? convertKmToMiles(contractorData.service_radius_km) : contractorData.service_radius_km} {user?.country === 'UK' ? 'miles' : 'km'} from your location
                  </p>
                </div>

                <div>
                  <Label>Base Location</Label>
                  {addresses.length > 0 ? (
                    <div className="border border-neutral-200 rounded-lg p-4 mt-2">
                      <p className="font-semibold">{addresses[0].label}</p>
                      <p className="text-sm text-neutral-600">{addresses[0].address_line_1}, {addresses[0].city}</p>
                    </div>
                  ) : (
                    <Button variant="outline" onClick={() => navigate('/addresses/new')} className="mt-2">
                      Add Base Location
                    </Button>
                  )}
                </div>

                <Button
                  onClick={handleContractorUpdate}
                  disabled={loading}
                  className="w-full bg-[#007AFF] hover:bg-[#0066CC] text-white rounded-full h-12"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </motion.div>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default ProfileSettings;
